import posthog from "posthog-js";
import { axiosBasic, getUserInfo } from "../../../../api/axios";
import { config } from "../../../../api/endPoints";
import { actionTypes } from "./officeActionTypes";
import { Common } from "../../../../utils";
import {
  getSelectedCompany,
} from "../../../Global/CustomFunctions";
import { convertToAlpha2 } from "../../../../utils/countryCodeMapping";

const { Countries, Office } = config.REST_API;

const dispatchCompanyOfficesChanged = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("company-offices-changed"));
  }
};

export const isLoading = value => {
  return { type: actionTypes.LOADING, value };
};

export const isOfficesLoading = value => {
  return { type: actionTypes.OFFICES_LOADING, value };
};

export const setOfficeSelected = () => {
  return { type: actionTypes.SET_OFFICE_SELECTED };
};

export const removeOfficeSelected = () => {
  return { type: actionTypes.REMOVE_OFFICE_SELECTED };
};
export const clearOfcErrors = () => {
  return { type: actionTypes.CLEAR_OFFICE_ERRORS };
};
export const setOffices = (offices, companyId) => {
  return { type: actionTypes.SET_OFFICES, offices, companyId };
};

/** Call after adding a user to an office so the office dashboard shows updated employee count without refetching. */
export const incrementOfficeUserCount = (officeId) => ({
  type: actionTypes.INCREMENT_OFFICE_USER_COUNT,
  officeId,
});

export const getAllOffices = companyId => {
  return dispatch => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { companyId };
    dispatch(isOfficesLoading(true));
    return axiosBasic
      .get(Office.GetCompanyOffices + "?companyId=" + companyId)
      .then(response => {
        let res = response.data;

        // Filter out inactive/deleted offices if the API returns them
        if (res.success && res.data && Array.isArray(res.data)) {
          // Filter out offices that are inactive, deleted, or have isActive: false
          const activeOffices = res.data.filter(office => {
            // Check for various possible inactive flags
            const isInactive = office.isActive === false ||
              office.isDeleted === true ||
              office.isDeactivated === true ||
              office.status === 'Inactive' ||
              office.status === 'Deleted';

            return !isInactive;
          });

          // Update the response with only active offices
          res.data = activeOffices;
        }

        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("getAllOffices", {
            type: res.success ? "info" : "exception",
            userId,
            success: res.success,
            userInput,
            reason: res.success ? undefined : res.message,
          });
        }

        // When API returns success: false, component won't call setOffices — clear loader here
        if (!res.success) {
          dispatch(isOfficesLoading(false));
        }
        return res;
      })
      .catch(error => {
        dispatch(isOfficesLoading(false));
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("getAllOffices", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          });
        }
        return error;
      });
  };
};
export const onAddOffice = (office, history, { redirect = true } = {}) => {
  return dispatch => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { name: office?.name, countryId: office?.countryId, stateId: office?.stateId, companyId: office?.companyId };
    dispatch(isLoading(true));
    return axiosBasic
      .post(Office.Add, office)
      .then(response => {
        const { success, message } = response.data;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("onAddOffice", {
            type: success ? "info" : "exception",
            userId,
            success,
            userInput,
            reason: success ? undefined : message,
          });
        }
        if (success) {
          dispatchCompanyOfficesChanged();
          let selectedCompany = getSelectedCompany();
          let link = `/${selectedCompany.name}/offices`;
          if (redirect) history.push(link);
          Common.showSuccessMessage("Workplace Created Successfully");
        } else {
          Common.showErrorMessage(message);
        }
        let res = response.data;
        return res;
      })
      .catch(error => {
        dispatch(isLoading(false));
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("onAddOffice", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          });
        }
        return error;
      });
  };
};

export const onGetCountries = () => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = {};
  dispatch(isLoading(true));
  axiosBasic
    .get(Countries.GetCountries)
    .then((response) => {
      const { success, data, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetCountries", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        // Define priority countries (order matters - first is highest priority)
        const priorityCountries = [
          'United States', 'USA', 'United States of America',
          'United Kingdom', 'UK', 'Great Britain',
          'Australia',
          'India',
          'Pakistan',
          'Canada',
          'Germany',
          'France',
          'Japan',
          'China',
          'Brazil',
          'Mexico',
          'Italy',
          'Spain',
          'Netherlands',
          'Sweden',
          'Norway',
          'Denmark',
          'Switzerland',
          'Singapore',
          'New Zealand',
          'South Africa',
          'United Arab Emirates',
          'Saudi Arabia',
          'Turkey'
        ];

        // Map countries and add priority score
        const countriesWithPriority = data.map((i, index) => {
          // Check if the code is already 2 letters (alpha2) or 3 letters (alpha3)
          const countryCode = i.alphaCode3 || i.alphaCode2;
          let alpha2Code;

          if (countryCode) {
            if (countryCode.length === 2) {
              // It's already a 2-letter code
              alpha2Code = countryCode.toLowerCase();
            } else if (countryCode.length === 3) {
              // It's a 3-letter code, convert it
              alpha2Code = convertToAlpha2(countryCode);
            }
          }

          // Calculate priority score (lower number = higher priority)
          const priorityIndex = priorityCountries.findIndex(priorityName =>
            i.name.toLowerCase().includes(priorityName.toLowerCase())
          );
          const priorityScore = priorityIndex >= 0 ? priorityIndex : 999; // High number for non-priority countries

          // Clean the country name to remove any alpha codes
          let cleanCountryName = i.name;

          // Remove common patterns like "Country Name (XX)" or "Country Name - XX"
          cleanCountryName = cleanCountryName.replace(/\s*[\(\[].{2,3}[\)\]]\s*$/g, ''); // Remove (XX) or [XX]
          cleanCountryName = cleanCountryName.replace(/\s*-\s*[A-Z]{2,3}\s*$/g, ''); // Remove - XX
          cleanCountryName = cleanCountryName.replace(/\s+[A-Z]{2,3}\s*$/g, ''); // Remove XX at end
          cleanCountryName = cleanCountryName.trim(); // Remove extra spaces

          const countryOption = {
            key: i.id,
            text: cleanCountryName,
            value: i.id,
            flag: alpha2Code,
            priorityScore: priorityScore
          };
          return countryOption;
        });

        // Sort countries by priority score
        const countries = countriesWithPriority.sort((a, b) => a.priorityScore - b.priorityScore);
        dispatch(showCountries(countries));
      } else {
        Common.showErrorMessage(message);
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetCountries", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage(error);
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

export const showCountries = (countries) => ({
  type: actionTypes.GET_COUNTRIES,
  payload: countries,
});

export const onGetStatesByCountryId = (id) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { countryId: id };
  dispatch(isLoading(true));
  axiosBasic
    .get(`${Office.State}?countryId=${id}`)
    .then((response) => {
      const { success, data, total } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetStatesByCountryId", {
          type: success ? "info" : "exception",
          userId,
          success: !!success,
          userInput,
        });
      }
      if (success && total > 0) {
        const states = data.map((i) => ({ key: i.id, text: i.name, value: i.id }));
        dispatch(showStates(states));
      } else {
        dispatch(showStates([]));
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetStatesByCountryId", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage(error);
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

export const showStates = (states) => ({
  type: actionTypes.GET_STATES,
  payload: states,
});

export const onGetBusinessDaysPresets = () => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = {};
  dispatch(isLoading(true));
  return axiosBasic
    .get(Office.BusinessDaysPresets)
    .then((response) => {
      const { success, data, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetBusinessDaysPresets", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        const businessDays = data.map((i) => ({ key: i.value, text: i.label, value: i.value }));
        dispatch({
          type: actionTypes.GET_BUSINESS_DAYS_PRESETS,
          payload: businessDays
        });
      } else {
        Common.showErrorMessage(message || "Failed to load business days presets");
      }
      dispatch(isLoading(false));
      return response.data;
    })
    .catch((error) => {
      dispatch(isLoading(false));
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetBusinessDaysPresets", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage("Failed to load business days presets");
      console.log(error);
      return error;
    });
};

export const onGetOfficeBusinessDays = (officeId) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { officeId };
  dispatch(isLoading(true));
  return axiosBasic
    .get(`${Office.GetOfficeBusinessDays}?officeId=${officeId}`)
    .then((response) => {
      const { success, data, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetOfficeBusinessDays", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        dispatch({
          type: actionTypes.GET_OFFICE_BUSINESS_DAYS,
          payload: { officeId, businessDays: data }
        });
      } else {
        Common.showErrorMessage(message || "Failed to load office business days");
      }
      dispatch(isLoading(false));
      return response.data;
    })
    .catch((error) => {
      dispatch(isLoading(false));
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetOfficeBusinessDays", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage("Failed to load office business days");
      return error;
    });
};

// Edit Office Actions
export const onGetOfficeByCompany = (officeId) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { officeId };
  dispatch(isLoading(true));
  axiosBasic
    .get(`${Office.GetOfficeByCompany}?officeId=${officeId}`)
    .then((response) => {
      const { success, data, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetOfficeByCompany", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        dispatch(setOfficeForEdit(data));
      } else {
        Common.showErrorMessage(message);
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetOfficeByCompany", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage("Something went wrong, please try again");
      console.log(error);
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

export const setOfficeForEdit = (office) => ({
  type: actionTypes.SET_OFFICE_FOR_EDIT,
  payload: office,
});

export const clearOfficeForEdit = () => ({
  type: actionTypes.CLEAR_OFFICE_FOR_EDIT,
});

export const onEditOffice = (office, history) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { officeId: office?.id ?? office?.Id, name: office?.name, countryId: office?.countryId, stateId: office?.stateId, companyId: office?.companyId };
  dispatch(isLoading(true));
  return axiosBasic
    .put(Office.Edit, office)
    .then((response) => {
      const { success, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onEditOffice", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        Common.showSuccessMessage("Workplace Updated Successfully");
        dispatchCompanyOfficesChanged();
        // Refresh the offices list
        const selectedCompany = getSelectedCompany();
        if (selectedCompany) {
          dispatch(getAllOffices(selectedCompany.id));
        }
      } else {
        Common.showErrorMessage(message);
      }
      return response.data;
    })
    .catch((error) => {
      dispatch(isLoading(false));
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onEditOffice", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage("Something went wrong, please try again");
      return error;
    });
};

export const onDeleteOffice = (officeId, options = {}) => (dispatch, getState) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { officeId, ...options };
  dispatch(isLoading(true));
  return axiosBasic
    .delete(`${Office.Delete}/${officeId}/remove`, { data: options })
    .then((response) => {
      const { success, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onDeleteOffice", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        Common.showSuccessMessage("Workplace Deleted Successfully");
        dispatchCompanyOfficesChanged();
        dispatch(isLoading(false));

        // Immediately remove the office from the current state
        const currentState = getState();
        const currentOffices = currentState.officeReducer.offices || [];
        const updatedOffices = currentOffices.filter(office =>
          (office.id || office.Id) !== officeId
        );
        dispatch(setOffices(updatedOffices));

        // Refresh the offices list from server and clear loader when done
        const selectedCompany = getSelectedCompany();
        if (selectedCompany) {
          dispatch(getAllOffices(selectedCompany.id))
            .then((res) => {
              if (res && res.success && res.data) {
                dispatch(setOffices(res.data, selectedCompany.id));
              } else {
                dispatch(isOfficesLoading(false));
              }
            })
            .catch(() => {
              dispatch(isOfficesLoading(false));
            });
        }
      } else {
        Common.showErrorMessage(message);
        dispatch(isLoading(false));
      }
      return response.data;
    })
    .catch((error) => {
      dispatch(isLoading(false));
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onDeleteOffice", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage("Something went wrong, please try again");
      return error;
    });
};

// export const offices = (offices) => ({
//   type: OFFICES,
//   payload: offices,
// });